import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPut } from '../utils/api';

type PlayerLevelRow = {
  id: string;
  name: string;
  sport: 'TENNIS' | 'PADEL' | 'UNKNOWN';
  format: 'SINGLES' | 'DOUBLES' | 'UNKNOWN';
  masteryOrder: number;
  singlesCount: number;
  doublesCount: number;
  totalCount: number;
};

const Levels: React.FC = () => {
  const [rows, setRows] = useState<PlayerLevelRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<PlayerLevelRow | null>(null);
  const [levelInput, setLevelInput] = useState('');
  const [sportInput, setSportInput] = useState<'TENNIS' | 'PADEL' | 'UNKNOWN'>('UNKNOWN');
  const [formatInput, setFormatInput] = useState<'SINGLES' | 'DOUBLES' | 'UNKNOWN'>('UNKNOWN');
  const [masteryOrderInput, setMasteryOrderInput] = useState<string>('0');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchLevels = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiGet<PlayerLevelRow[]>('/players/levels');
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      console.error('Ошибка загрузки уровней игроков:', e);
      setRows([]);
      setError(e?.message || 'Не удалось загрузить уровни игроков');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const openEditModal = (row: PlayerLevelRow) => {
    setSelectedRow(row);
    setLevelInput(row.name);
    setSportInput(row.sport);
    setFormatInput(row.format);
    setMasteryOrderInput(String(row.masteryOrder));
    setSaveError(null);
  };

  const closeEditModal = (force = false) => {
    if (isSaving && !force) return;
    setSelectedRow(null);
    setLevelInput('');
    setSportInput('UNKNOWN');
    setFormatInput('UNKNOWN');
    setMasteryOrderInput('0');
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!selectedRow) return;
    const nextLevel = levelInput.trim();
    if (!nextLevel) {
      setSaveError('Уровень не может быть пустым.');
      return;
    }
    const parsedMasteryOrder = Number(masteryOrderInput);
    if (!Number.isFinite(parsedMasteryOrder)) {
      setSaveError('Mastery order должен быть числом.');
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);
      await apiPut(`/players/levels/${encodeURIComponent(selectedRow.name)}`, {
        id: selectedRow.id,
        level: nextLevel,
        sport: sportInput,
        format: formatInput,
        masteryOrder: Math.round(parsedMasteryOrder)
      });
      await fetchLevels();
      closeEditModal(true);
    } catch (e: any) {
      console.error('Ошибка обновления уровня:', e);
      setSaveError(e?.message || 'Не удалось обновить уровень');
    } finally {
      setIsSaving(false);
    }
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.singles += row.singlesCount || 0;
        acc.doubles += row.doublesCount || 0;
        return acc;
      },
      { singles: 0, doubles: 0 }
    );
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Справочник уровней</h1>
          <p className="text-sm text-gray-600">
            Текущие уровни, которые назначены игрокам в системе.
          </p>
        </div>
        <button
          onClick={fetchLevels}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          disabled={loading}
        >
          {loading ? 'Обновление...' : 'Обновить'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500">Уровней найдено</div>
          <div className="text-2xl font-semibold text-gray-900">{rows.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500">Назначений одиночки</div>
          <div className="text-2xl font-semibold text-gray-900">{totals.singles}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs text-gray-500">Назначений пары</div>
          <div className="text-2xl font-semibold text-gray-900">{totals.doubles}</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sport</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Format</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Mastery order</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Одиночка</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Пары</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Всего игроков</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="px-4 py-2 text-sm text-gray-700">{row.id}</td>
                <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                  <button
                    type="button"
                    onClick={() => openEditModal(row)}
                    className="text-blue-700 hover:text-blue-900 hover:underline"
                  >
                    {row.name}
                  </button>
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">{row.sport}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{row.format}</td>
                <td className="px-4 py-2 text-sm text-right text-gray-700">{row.masteryOrder}</td>
                <td className="px-4 py-2 text-sm text-right text-gray-700">{row.singlesCount}</td>
                <td className="px-4 py-2 text-sm text-right text-gray-700">{row.doublesCount}</td>
                <td className="px-4 py-2 text-sm text-right text-gray-900">{row.totalCount}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">
                  Нет данных по уровням игроков.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-lg font-semibold text-gray-900">Редактирование уровня</h2>
              <button
                type="button"
                onClick={closeEditModal}
                className="text-gray-500 hover:text-gray-700"
                disabled={isSaving}
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 px-4 py-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">ID</label>
                <input
                  value={selectedRow.id}
                  readOnly
                  disabled
                  className="w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Name</label>
                <input
                  value={levelInput}
                  onChange={(e) => setLevelInput(e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Sport</label>
                <select
                  value={sportInput}
                  onChange={(e) => setSportInput(e.target.value as 'TENNIS' | 'PADEL' | 'UNKNOWN')}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  <option value="TENNIS">TENNIS</option>
                  <option value="PADEL">PADEL</option>
                  <option value="UNKNOWN">UNKNOWN</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Format</label>
                <select
                  value={formatInput}
                  onChange={(e) => setFormatInput(e.target.value as 'SINGLES' | 'DOUBLES' | 'UNKNOWN')}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  <option value="SINGLES">SINGLES</option>
                  <option value="DOUBLES">DOUBLES</option>
                  <option value="UNKNOWN">UNKNOWN</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Mastery order</label>
                <input
                  value={masteryOrderInput}
                  onChange={(e) => setMasteryOrderInput(e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>

              {saveError && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {saveError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t px-4 py-3">
              <button
                type="button"
                onClick={closeEditModal}
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                disabled={isSaving}
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-gray-400"
                disabled={isSaving}
              >
                {isSaving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Levels;
